import { traducirError, useTraduccion } from '@tallerya/i18n';
import { useAvisos, type ElementoMenu } from '@tallerya/ui';
import { useState } from 'react';
import { usePermisos } from '../../permisos/usePermiso';
import { useCambiosGasto, type Gasto, type MontoGasto } from './datos';
import { VentanaGasto, type DestinoGasto } from './VentanaGasto';
import { EliminarGasto, MontoDeGasto, PagarGasto } from './VentanasDeGasto';

// El «•••» de un gasto y sus ventanas, los mismos en la sección de la orden
// (#143), en el listado de la caja (#144) y en la ficha del gasto (#189): editar,
// cambiar el monto, marcar pagado o quitar la marca, y eliminar. También el alta,
// con su destino.
//
// - Editar, el pago y eliminar piden `expense.edit`; el monto, `expense.amount.edit`
//   y que se haya leído —su versión es la que pide cambiarlo—.
// - Quitar la marca de pagado no tiene ventana: si la API lo rechaza, lo dice
//   `problema`, y la pantalla lo muestra donde le quede.

type Abierta =
  // `pendiente`: lo que no se pudo al registrarlo, si la ventana pasó a editarlo.
  | { tipo: 'gasto'; gasto: 'nuevo' | string; pendiente?: string }
  // El monto se guarda al abrir: su versión es la que pide cambiarlo.
  | { tipo: 'monto'; gasto: Gasto; monto: MontoGasto }
  | { tipo: 'pagar' | 'eliminar'; gasto: Gasto }
  | null;

/**
 * `alEliminar`: lo que hace la pantalla cuando el gasto ya no existe. La ficha
 * sale; una lista no lo pasa, porque la fila se va sola.
 */
export function useAccionesGasto(destino: DestinoGasto, alEliminar?: () => void) {
  const { t } = useTraduccion();
  const { puede } = usePermisos();
  const { avisar } = useAvisos();
  const cambios = useCambiosGasto();
  const edita = puede('expense.edit');
  const editaMontos = puede('expense.amount.view') && puede('expense.amount.edit');

  const [abierta, setAbierta] = useState<Abierta>(null);
  const [problema, setProblema] = useState<string | null>(null);
  /** El gasto con un toque en camino: su «•••» espera. */
  const [enCurso, setEnCurso] = useState<string | null>(null);

  const cerrar = () => setAbierta(null);
  const gastoDe = (tipo: 'pagar' | 'eliminar') => (abierta?.tipo === tipo ? abierta.gasto : null);

  async function quitarPago(gasto: Gasto) {
    setEnCurso(String(gasto.id));
    try {
      const rechazo = await cambios.quitarPago(String(gasto.id));
      setProblema(
        rechazo ? (rechazo.code ? traducirError(rechazo.code, t) : t('ui.formulario.aviso.sinRespuesta')) : null,
      );
      if (!rechazo) avisar({ titulo: t('admin.gastos.aviso.pagoQuitado') });
    } catch {
      setProblema(t('ui.formulario.aviso.sinRespuesta'));
    } finally {
      setEnCurso(null);
    }
  }

  /**
   * Lo del «•••» de un gasto. `monto`, si se leyó: sin él no se ofrece cambiarlo.
   * `conEditar: false` lo deja fuera, para quien lo tiene en su propio botón: la ficha.
   */
  function acciones(gasto: Gasto, monto: MontoGasto | undefined, { conEditar = true } = {}): ElementoMenu[] {
    return [
      ...(edita && conEditar
        ? [
            {
              etiqueta: t('admin.gastos.editar'),
              onSeleccionar: () => setAbierta({ tipo: 'gasto', gasto: String(gasto.id) }),
            },
          ]
        : []),
      ...(editaMontos && monto
        ? [{ etiqueta: t('admin.gastos.cambiarMonto'), onSeleccionar: () => setAbierta({ tipo: 'monto', gasto, monto }) }]
        : []),
      ...(edita
        ? [
            gasto.paidOn
              ? { etiqueta: t('admin.gastos.quitarPago'), onSeleccionar: () => void quitarPago(gasto) }
              : { etiqueta: t('admin.gastos.marcarPagado'), onSeleccionar: () => setAbierta({ tipo: 'pagar', gasto }) },
            { tipo: 'divisor' as const },
            {
              etiqueta: t('admin.gastos.eliminar'),
              peligro: true,
              onSeleccionar: () => setAbierta({ tipo: 'eliminar', gasto }),
            },
          ]
        : []),
    ];
  }

  const ventanas = (
    <>
      <VentanaGasto
        destino={destino}
        gasto={abierta?.tipo === 'gasto' ? abierta.gasto : null}
        pendiente={abierta?.tipo === 'gasto' ? abierta.pendiente : undefined}
        onCerrar={cerrar}
        onEditar={(nuevo, pendiente) => setAbierta({ tipo: 'gasto', gasto: nuevo, pendiente })}
      />
      <MontoDeGasto
        gasto={abierta?.tipo === 'monto' ? abierta.gasto : null}
        monto={abierta?.tipo === 'monto' ? abierta.monto : undefined}
        onCerrar={cerrar}
      />
      <PagarGasto gasto={gastoDe('pagar')} onCerrar={cerrar} />
      <EliminarGasto gasto={gastoDe('eliminar')} onCerrar={cerrar} onEliminado={alEliminar} />
    </>
  );

  return {
    acciones,
    /** Abre el alta, con el destino que se le dio. */
    registrar: () => setAbierta({ tipo: 'gasto', gasto: 'nuevo' }),
    /** Abre la edición, si se puede editar: tocar la fila de una lista. */
    editar: edita ? (gasto: Gasto) => setAbierta({ tipo: 'gasto', gasto: String(gasto.id) }) : undefined,
    problema,
    enCurso,
    /** Las ventanas: van una vez en la pantalla, donde sea. */
    ventanas,
  };
}
